import { useState } from 'react'
import type { ExerciseMcq } from '../types'
import { useTranslation } from 'react-i18next'
import { useExerciseSlot } from '../hooks/useExerciseSet'

interface ExerciseMCQProps {
  exercise: ExerciseMcq
  /** Position dans la série d'exercices du message — voir `ExerciseSetProvider`. */
  slotIndex: number
}

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F']
const NO_ANSWER = '—'

function optionLetter(idx: number): string {
  return OPTION_LABELS[idx] ?? String(idx + 1)
}

export function ExerciseMCQ({ exercise, slotIndex }: Readonly<ExerciseMCQProps>) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<number[]>([])

  const hasAnswer = selected.length > 0

  const { locked: submitted } = useExerciseSlot(slotIndex, hasAnswer, () => {
    const sorted = [...selected].toSorted((a, b) => a - b)
    const isCorrect =
      selected.length > 0 &&
      selected.length === exercise.correct.length &&
      selected.every((i) => exercise.correct.includes(i))

    return {
      itemType: 'mcq',
      question: exercise.question,
      answerLabel:
        sorted.length > 0 ? sorted.map((i) => exercise.options[i] ?? '').join(', ') : NO_ANSWER,
      correct: isCorrect,
      answered: hasAnswer,
    }
  })

  const toggle = (idx: number) => {
    if (submitted) return
    if (exercise.multiple) {
      setSelected((prev) =>
        prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
      )
    } else {
      setSelected([idx])
    }
  }

  const isCorrect = (idx: number) => exercise.correct.includes(idx)
  const allCorrect =
    submitted &&
    selected.length === exercise.correct.length &&
    selected.every((i) => exercise.correct.includes(i))

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-[var(--color-neutral-900)]">{exercise.question}</p>

      <div
        className="flex flex-col gap-2"
        role={exercise.multiple ? 'group' : 'radiogroup'}
        aria-label={exercise.question}
      >
        {exercise.options.map((option, idx) => {
          const isSelected = selected.includes(idx)
          let borderColor = 'border-gray-200 hover:border-[var(--color-primary)]'
          let bgColor = 'bg-white hover:bg-[var(--color-primary-subtle)]'
          let textColor = 'text-[var(--color-neutral-800)]'
          let indicatorStyle = 'border-gray-300'

          if (submitted) {
            if (isSelected && isCorrect(idx)) {
              borderColor = 'border-[var(--color-success)]'
              bgColor = 'bg-[var(--color-success-light)]'
              textColor = 'text-[var(--color-success)]'
              indicatorStyle = 'border-[var(--color-success)] bg-[var(--color-success)]'
            } else if (isSelected && !isCorrect(idx)) {
              borderColor = 'border-[var(--color-error)]'
              bgColor = 'bg-[var(--color-error-light)]'
              textColor = 'text-[var(--color-error)]'
              indicatorStyle = 'border-[var(--color-error)] bg-[var(--color-error)]'
            } else if (!isSelected && isCorrect(idx)) {
              borderColor = 'border-[var(--color-success)]'
              bgColor = 'bg-[var(--color-success-light)] opacity-60'
              indicatorStyle = 'border-[var(--color-success)]'
            }
          } else if (isSelected) {
            borderColor = 'border-[var(--color-primary)]'
            bgColor = 'bg-[var(--color-primary-light)]'
            textColor = 'text-[var(--color-primary)]'
            indicatorStyle = 'border-[var(--color-primary)] bg-[var(--color-primary)]'
          }

          return (
            <button
              key={idx}
              type="button"
              role={exercise.multiple ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              disabled={submitted}
              onClick={() => toggle(idx)}
              className={`flex items-center gap-3 w-full min-h-[44px] px-3 py-2.5 rounded-lg border transition-all text-left cursor-pointer disabled:cursor-default ${borderColor} ${bgColor}`}
            >
              <span
                className={`shrink-0 flex items-center justify-center w-4 h-4 rounded-${exercise.multiple ? 'sm border-2' : 'full border-2'} transition-all ${indicatorStyle}`}
                aria-hidden="true"
              >
                {isSelected && (
                  <span
                    className={`${exercise.multiple ? 'w-2 h-2 rounded-sm' : 'w-1.5 h-1.5 rounded-full'} bg-white`}
                  />
                )}
              </span>
              <span className={`text-sm flex-1 ${textColor}`}>
                <span className="font-semibold mr-1">{optionLetter(idx)}.&nbsp;</span>
                {option}
              </span>
              {submitted && isSelected && isCorrect(idx) && (
                <span aria-hidden="true" className="text-[var(--color-success)] text-xs font-bold">✓</span>
              )}
              {submitted && isSelected && !isCorrect(idx) && (
                <span aria-hidden="true" className="text-[var(--color-error)] text-xs font-bold">✗</span>
              )}
            </button>
          )
        })}
      </div>

      {submitted && exercise.explanation && (
        <div className="mt-2 rounded-lg px-3 py-2 text-xs bg-[var(--color-primary-light)] text-[var(--color-neutral-700)]">
          <span className="font-semibold">{t('session.explanation')} </span>
          {exercise.explanation}
        </div>
      )}

      {submitted && (
        <p
          className={`text-xs font-medium ${allCorrect ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}
        >
          {allCorrect ? t('session.exercise_mcq_correct') : t('session.exercise_mcq_incorrect')}
        </p>
      )}
    </div>
  )
}
